import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

const BADGE_COLORS = {
  streak:      { bg: 'linear-gradient(135deg, #FF9500, #FF5E3A)', ring: '#FF9500' },
  completions: { bg: 'linear-gradient(135deg, #7EA86C, #5C8748)', ring: '#7EA86C' },
  mastery:     { bg: 'linear-gradient(135deg, #CE82FF, #A855F7)', ring: '#CE82FF' },
  time:        { bg: 'linear-gradient(135deg, #1CB0F6, #0284C7)', ring: '#1CB0F6' },
  forest:      { bg: 'linear-gradient(135deg, #2DD4BF, #0D9488)', ring: '#2DD4BF' },
  engagement:  { bg: 'linear-gradient(135deg, #FF6B8A, #E11D48)', ring: '#FF6B8A' },
};

/* ── Rarity tiers based on achievement threshold ── */
const getRarity = (threshold) => {
  if (threshold <= 10) return 'low';
  if (threshold <= 50) return 'medium';
  return 'high';
};

const RARITY_CONFIG = {
  low:    { phaseExtra: 0,   confettiCount: 60,  hapticBursts: 1 },
  medium: { phaseExtra: 150, confettiCount: 120, hapticBursts: 2 },
  high:   { phaseExtra: 350, confettiCount: 200, hapticBursts: 3 },
};

/* ── Milestone stat text ── */
const getStatLine = (achievement, isIt, isEs) => {
  const v = achievement.current ?? achievement.threshold;
  const g = achievement.group;
  if (g === 'streak')      return isIt ? `La tua serie migliore: ${v} giorni 🔥`      : isEs ? `Tu mejor racha: ${v} días 🔥`              : `Your best streak: ${v} days 🔥`;
  if (g === 'completions') return isIt ? `${v} gesti completati! 💪`                    : isEs ? `¡${v} gestos completados! 💪`                  : `${v} gestures completed! 💪`;
  if (g === 'mastery')     return isIt ? `${v} gesti in un'area! 🎯`                    : isEs ? `¡${v} gestos en un área! 🎯`                   : `${v} gestures in one area! 🎯`;
  if (g === 'time')        return isIt ? `${Math.round(v / 60)} ore investite! ⏱️`      : isEs ? `¡${Math.round(v / 60)} horas invertidas! ⏱️`   : `${Math.round(v / 60)} hours invested! ⏱️`;
  if (g === 'forest')      return isIt ? `Livello foresta ${v}! 🌳`                     : isEs ? `¡Nivel del bosque ${v}! 🌳`                    : `Forest level ${v}! 🌳`;
  if (g === 'engagement')  return isIt ? `Punteggio impegno: ${v}! ❤️`                  : isEs ? `¡Puntuación de compromiso: ${v}! ❤️`           : `Engagement score: ${v}! ❤️`;
  return '';
};

const haptic = (style) => { try { Haptics.impact({ style }); } catch (_) {} };

const AchievementCelebrationModal = ({ achievement, onClose }) => {
  const { i18n } = useTranslation();
  const isIt = i18n.language === 'it';
  const isEs = i18n.language === 'es';

  // Phase: 0=hidden, 1=backdrop+orb, 2=badge morph, 3=icon+stars+title, 4=desc+buttons+confetti
  const [phase, setPhase] = useState(0);
  const timersRef = useRef([]);
  const confettiCanvasRef = useRef(null);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const fireConfetti = useCallback((colors, count) => {
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:10001;background:transparent;';
    document.body.appendChild(canvas);
    confettiCanvasRef.current = canvas;
    const fire = confetti.create(canvas, { resize: true });
    fire({
      particleCount: count,
      spread: 80,
      startVelocity: 35,
      origin: { y: 0.55 },
      colors: [colors.ring, '#FFD700', '#ffffff'],
      ticks: 80,
      zIndex: 10001,
    });
    setTimeout(() => { try { canvas.remove(); } catch (_) {} }, 3500);
  }, []);

  useEffect(() => {
    if (!achievement) { setPhase(0); return; }

    const rarity = getRarity(achievement.threshold || 1);
    const cfg = RARITY_CONFIG[rarity];
    const extra = cfg.phaseExtra;

    // Phase 1: backdrop + mystery orb
    const t1 = setTimeout(() => {
      setPhase(1);
      haptic(ImpactStyle.Light);
    }, 50);

    // Phase 2: orb → badge morph
    const t2 = setTimeout(() => {
      setPhase(2);
      haptic(ImpactStyle.Medium);
    }, 1200 + extra);

    // Phase 3: icon + stars + title
    const t3 = setTimeout(() => {
      setPhase(3);
      haptic(ImpactStyle.Heavy);
      if (cfg.hapticBursts >= 2) setTimeout(() => haptic(ImpactStyle.Medium), 120);
      if (cfg.hapticBursts >= 3) setTimeout(() => haptic(ImpactStyle.Light), 240);
    }, 1800 + extra * 2);

    // Phase 4: description + buttons + confetti
    const t4 = setTimeout(() => {
      setPhase(4);
      const colors = BADGE_COLORS[achievement.group] || BADGE_COLORS.completions;
      fireConfetti(colors, cfg.confettiCount);
    }, 2400 + extra * 3);

    timersRef.current = [t1, t2, t3, t4];
    return clearTimers;
  }, [achievement, fireConfetti, clearTimers]);

  if (!achievement) return null;

  const title = isIt ? achievement.title_it : achievement.title_en;
  const desc = isIt ? achievement.desc_it : achievement.desc_en;
  const colors = BADGE_COLORS[achievement.group] || BADGE_COLORS.completions;
  const statLine = getStatLine(achievement, isIt, isEs);
  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  const handleClose = () => {
    clearTimers();
    setPhase(0);
    try { confettiCanvasRef.current?.remove(); } catch (_) {}
    setTimeout(onClose, 200);
  };

  const handleShare = async () => {
    const shareTitle = isIt ? 'Traguardo Sbloccato!' : isEs ? '¡Logro desbloqueado!' : 'Achievement Unlocked!';
    const shareText = isIt
      ? `Ho appena sbloccato "${title}" su BetterHusband! 🏆🔥`
      : isEs
      ? `¡Acabo de desbloquear "${title}" en BetterHusband! 🏆🔥`
      : `I just unlocked "${title}" on BetterHusband! 🏆🔥`;
    try {
      await navigator.share({ title: shareTitle, text: shareText });
    } catch (_) {}
  };

  return (
    <div
      onClick={phase >= 3 ? handleClose : undefined}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: phase >= 1 ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0)',
        backdropFilter: phase >= 1 ? 'blur(4px)' : 'none',
        opacity: phase >= 1 ? 1 : 0,
        transition: 'background 0.4s ease, backdrop-filter 0.4s ease, opacity 0.3s ease',
        padding: 24,
        pointerEvents: phase >= 1 ? 'auto' : 'none',
      }}
    >
      <style>{`
        @keyframes achOrbPulse {
          0%, 100% { transform: translate(-50%,-50%) scale(1);   opacity: 0.9; }
          50%      { transform: translate(-50%,-50%) scale(1.15); opacity: 1; }
        }
        @keyframes achBadgeBounce {
          0%   { transform: scale(0.3) rotate(-15deg); opacity: 0; }
          50%  { transform: scale(1.15) rotate(3deg); opacity: 1; }
          70%  { transform: scale(0.95) rotate(-1deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes achStarFloat {
          0%   { transform: scale(0) rotate(0deg); opacity: 0; }
          40%  { transform: scale(1.2) rotate(15deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 0.9; }
        }
        @keyframes achPulseGlow {
          0%, 100% { box-shadow: 0 0 24px ${colors.ring}25, 0 0 60px ${colors.ring}10; }
          50%      { box-shadow: 0 0 32px ${colors.ring}35, 0 0 80px ${colors.ring}15; }
        }
        @keyframes achFadeSlideUp {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 340,
          background: 'var(--c-surface)', borderRadius: 24,
          border: `1px solid ${colors.ring}20`,
          padding: '36px 28px 28px', textAlign: 'center',
          transform: phase >= 1 ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)',
          transition: 'transform 0.35s cubic-bezier(.34,1.56,.64,1)',
        }}
      >
        {/* ── Phase 1+: Header text ── */}
        <div style={{
          fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 600,
          color: 'var(--c-texSec)', letterSpacing: '0.08em', textTransform: 'uppercase',
          marginBottom: 24,
          opacity: phase >= 1 ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}>
          {isIt ? 'Nuovo Traguardo!' : isEs ? '¡Nuevo Logro!' : 'New Achievement!'}
        </div>

        {/* ── Badge area: orb → badge morph ── */}
        <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 24px' }}>

          {/* Phase 1-2: Mystery orb (fades out when badge appears) */}
          {phase >= 1 && phase < 3 && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              width: phase >= 2 ? 80 : 60,
              height: phase >= 2 ? 80 : 60,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${colors.ring}90, ${colors.ring}20)`,
              animation: phase < 2 ? 'achOrbPulse 1.2s ease-in-out infinite' : 'none',
              transform: 'translate(-50%, -50%)',
              transition: 'width 0.5s cubic-bezier(.34,1.56,.64,1), height 0.5s cubic-bezier(.34,1.56,.64,1)',
              boxShadow: `0 0 40px ${colors.ring}40`,
            }} />
          )}

          {/* Phase 3+: Floating stars */}
          {phase >= 3 && [
            { top: 4, left: 8, size: 18, delay: '0s' },
            { top: 0, left: 36, size: 12, delay: '0.1s' },
            { top: 20, left: -4, size: 14, delay: '0.2s' },
            { top: 4, right: 8, size: 18, delay: '0.08s' },
            { top: 0, right: 36, size: 12, delay: '0.18s' },
            { top: 20, right: -4, size: 14, delay: '0.25s' },
            { bottom: 16, left: 12, size: 10, delay: '0.3s' },
            { bottom: 16, right: 12, size: 10, delay: '0.32s' },
          ].map((star, i) => (
            <Icon
              key={i}
              icon="solar:star-bold"
              style={{
                position: 'absolute', ...star, width: star.size, height: star.size,
                color: '#FFD700',
                animation: `achStarFloat 0.6s ${star.delay} cubic-bezier(.34,1.56,.64,1) both`,
                filter: 'drop-shadow(0 0 4px rgba(255,215,0,0.5))',
              }}
            />
          ))}

          {/* Phase 3+: Main badge */}
          {phase >= 3 && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 80, height: 80, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: colors.bg,
              border: `4px solid ${colors.ring}`,
              animation: 'achBadgeBounce 0.7s 0s cubic-bezier(.34,1.56,.64,1) both, achPulseGlow 2s 0.7s ease-in-out infinite',
            }}>
              <Icon icon={achievement.icon} style={{ width: 36, height: 36, color: '#fff', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
            </div>
          )}
        </div>

        {/* ── Phase 3+: Title ── */}
        {phase >= 3 && (
          <div style={{
            fontFamily: 'Manrope-Variable', fontSize: 20, fontWeight: 700,
            color: 'var(--c-texPri)', marginBottom: 8, lineHeight: 1.3,
            animation: 'achFadeSlideUp 0.4s ease both',
          }}>
            {title}
          </div>
        )}

        {/* ── Phase 4+: Description ── */}
        {phase >= 4 && (
          <div style={{
            fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 400,
            color: 'var(--c-texSec)', lineHeight: 1.5,
            marginBottom: 8, padding: '0 8px',
            animation: 'achFadeSlideUp 0.35s ease both',
          }}>
            {desc}
          </div>
        )}

        {/* ── Phase 4+: Milestone stat ── */}
        {phase >= 4 && statLine && (
          <div style={{
            fontFamily: 'Manrope-Variable', fontSize: 12, fontWeight: 600,
            color: colors.ring, lineHeight: 1.4,
            marginBottom: 24, padding: '0 8px',
            animation: 'achFadeSlideUp 0.35s 0.1s ease both',
          }}>
            {statLine}
          </div>
        )}

        {/* ── Phase 4+: Action buttons ── */}
        {phase >= 4 && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            animation: 'achFadeSlideUp 0.35s 0.15s ease both',
          }}>
            {canShare && (
              <button
                onClick={handleShare}
                style={{
                  width: '100%', maxWidth: 220, padding: '14px 32px', borderRadius: 999,
                  border: 'none', cursor: 'pointer', background: colors.bg,
                  fontFamily: 'Manrope-Variable', fontSize: 15, fontWeight: 700,
                  color: '#fff', letterSpacing: '0.02em',
                  boxShadow: `0 4px 16px ${colors.ring}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <Icon icon="solar:share-linear" style={{ width: 18, height: 18 }} />
                {isIt ? 'Condividi' : isEs ? 'Compartir' : 'Share'}
              </button>
            )}
            <button
              onClick={handleClose}
              style={{
                width: '100%', maxWidth: 220, padding: canShare ? '10px 32px' : '14px 32px',
                borderRadius: 999, border: 'none', cursor: 'pointer',
                background: canShare ? 'transparent' : colors.bg,
                fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 600,
                color: canShare ? 'var(--c-texSec)' : '#fff',
                letterSpacing: '0.02em',
                boxShadow: canShare ? 'none' : `0 4px 16px ${colors.ring}40`,
              }}
            >
              {isIt ? 'Continua' : isEs ? 'Continuar' : 'Continue'}
            </button>
          </div>
        )}

        {/* Spacer while content is loading in phases 1-3 */}
        {phase < 4 && <div style={{ height: 60 }} />}
      </div>
    </div>
  );
};

export default AchievementCelebrationModal;
